import { AxiosInstance } from 'axios';
import { ModuleDetails, ModuleOverview } from 'types/module';
import { Result } from 'services/Result';
import { ModuleTableOfContents } from 'types/learningHub/moduleContents';
import i18next from 'i18next';

export class LearningHubService {
  private axiosInstance: AxiosInstance;
  constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  getModules = async () => {
    const response = await this.axiosInstance.get<Result<{ modules: ModuleOverview[] }>>(`api/modules`);
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data.modules;
    }
    throw Error('There was an internal error while fetching modules.');
  };

  getModuleDetails = async (slug: string) => {
    const response = await this.axiosInstance.get<Result<ModuleDetails>>(`api/modules/${slug}`);
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching module details.');
  };

  getModuleContents = async (id: string) => {
    const response = await this.axiosInstance.get<Result<ModuleTableOfContents>>(`api/modules/${id}/contents`);
    if (response.data && response.data.data && !response.data.isError) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching module contents.');
  };

  getLearningHubTestContent = async (testId: string) => {
    const response = await this.axiosInstance.get<Result<any>>(`api/rhapsode-content/${testId}`);
    if (response.data && response.data.data && !response.data.isError) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching test contents.');
  };

  downloadCertificatePdf = async (level: string) => {
    const response = await this.axiosInstance.get(`/api/certificate-pdf/${level}`, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      responseType: 'blob',
    });

    if (response.data) {
      if (response.status === 400) {
        return {
          status: 'error',
          data: JSON.parse(await response.data.text()),
        };
      } else if (response.status === 200 && response.data.type === 'application/pdf') {
        return { status: 'success', data: response.data };
      }
    }
    throw Error(i18next.t('DownloadCertificateError', { level: level, ns: 'certificate' }));
  };

  download = async (level: string) => {
    try {
      await this.downloadCertificatePdf(level).then((response) => {
        if (response.status === 'error') {
          return;
        }
        const href = window.URL.createObjectURL(response.data);

        const anchorElement = document.createElement('a');

        anchorElement.href = href;
        anchorElement.download = `Trackman-Certificate-${level}`;
        anchorElement.style.display = 'none';

        document.body.appendChild(anchorElement);
        anchorElement.click();

        document.body.removeChild(anchorElement);
        window.URL.revokeObjectURL(href);
      });
    } catch (ex) {
      throw Error('Error');
    }
  };

  markResourceAsDone = async (contentId: string) => {
    const response = await this.axiosInstance.post<Result<any>>(`api/Progress/MarkResourceAsDone`, { contentId });
    if (response.status === 200) {
      return;
    }

    throw Error('There was an internal error while marking the resource as done.');
  };
}
export default LearningHubService;
